import React, { useState, useEffect, useMemo, useRef } from 'react';
import JSZip from 'jszip';
import { getAuth } from 'firebase/auth';
import { getBlob, getStorage, ref as storageRef } from 'firebase/storage';
import { getReceipts, deleteReceipt } from '../../services/receiptManagementService';
import { useLocation, useNavigate } from 'react-router-dom';
import { CATEGORY_OPTIONS, getCategoryDisplayName, normalizeCategoryId } from '../../lib/categories';
import { app } from '../../firebase';
import { API_ENDPOINTS, getApiUrl } from '../../lib/apiConfig';

const DEFAULT_RECEIPT_SORT = 'latestEntryDesc';

const RECEIPT_SORT_OPTIONS = [
  { id: 'latestEntryDesc', label: 'Latest entry first' },
  { id: 'latestEntryAsc', label: 'Oldest entry first' },
  { id: 'receiptDateDesc', label: 'Receipt date (newest)' },
  { id: 'receiptDateAsc', label: 'Receipt date (oldest)' }
];

const IMAGE_CONTENT_TYPE_TO_EXTENSION = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'application/pdf': 'pdf'
};

const FIREBASE_STORAGE_HOSTNAMES = [
  'firebasestorage.googleapis.com',
  'storage.googleapis.com'
];

/**
 * Receipt List Component
 *
 * This component displays a list of receipts with filtering and pagination.
 */
function ReceiptList({ activeWorkspace = null }) {
  const getFirebaseStoragePathFromUrl = (url) => {
    if (typeof url !== 'string' || !url.trim()) return '';

    try {
      const parsedUrl = new URL(url);
      const isFirebaseStorageUrl = FIREBASE_STORAGE_HOSTNAMES.some(
        (hostname) => parsedUrl.hostname.toLowerCase() === hostname
      );

      if (!isFirebaseStorageUrl) return '';

      const pathMatch = parsedUrl.pathname.match(/\/o\/([^/?#]+)/i);
      if (!pathMatch?.[1]) return '';

      return decodeURIComponent(pathMatch[1]);
    } catch {
      return '';
    }
  };

  const getReceiptStoragePath = (receipt, imageUrl) => {
    const directPathCandidates = [
      receipt?.storagePath,
      receipt?.imageStoragePath,
      receipt?.extractedData?.storagePath,
      receipt?.extractedData?.imageStoragePath
    ];

    const directPath = directPathCandidates.find((candidate) => typeof candidate === 'string' && candidate.trim());
    if (directPath) return directPath;

    return getFirebaseStoragePathFromUrl(imageUrl);
  };

  const downloadReceiptBlob = async (receipt, imageUrl) => {
    const storagePath = getReceiptStoragePath(receipt, imageUrl);
    let lastError = null;

    if (storagePath) {
      try {
        const storage = getStorage(app);
        const blob = await getBlob(storageRef(storage, storagePath));
        if (blob && blob.size > 0) {
          return blob;
        }
        lastError = new Error('Storage blob is empty');
      } catch (storageError) {
        lastError = storageError;
      }
    }

    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      return blob;
    } catch (fetchError) {
      if (lastError) {
        const storageMessage = lastError?.message || 'Storage download failed';
        const fetchMessage = fetchError?.message || 'URL download failed';
        throw new Error(`${storageMessage}; fallback failed: ${fetchMessage}`);
      }
      throw fetchError;
    }
  };

  const sanitizeFilenamePart = (value, fallback = 'receipt') => {
    const cleaned = String(value || '')
      .trim()
      .replace(/[<>:"/\\|?*]+/g, '_')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');

    return cleaned || fallback;
  };

  const formatDateForFilename = (value, fallback = 'undated') => {
    const parsed = new Date(value);
    if (!Number.isFinite(parsed.getTime())) return fallback;
    return parsed.toISOString().slice(0, 10);
  };

  const escapeCsvCell = (value) => {
    const cell = String(value ?? '');
    if (/[",\n]/.test(cell)) {
      return `"${cell.replace(/"/g, '""')}"`;
    }
    return cell;
  };

  const getImageExtension = (url, contentType = '') => {
    const normalizedContentType = String(contentType || '').toLowerCase().split(';')[0].trim();
    if (IMAGE_CONTENT_TYPE_TO_EXTENSION[normalizedContentType]) {
      return IMAGE_CONTENT_TYPE_TO_EXTENSION[normalizedContentType];
    }

    try {
      const { pathname } = new URL(url);
      const extensionMatch = pathname.match(/\.([a-z0-9]{2,5})$/i);
      if (extensionMatch) {
        return extensionMatch[1].toLowerCase();
      }
    } catch {
      // Ignore malformed URLs and use fallback extension.
    }

    return 'bin';
  };

  const getReceiptImageUrl = (receipt) => {
    const candidates = [
      receipt?.imageUrl,
      receipt?.receiptImageUrl,
      receipt?.extractedData?.imageUrl,
      receipt?.extractedData?.receiptImageUrl
    ];
    return candidates.find((candidate) => typeof candidate === 'string' && candidate.trim()) || '';
  };

  const ensureUniqueFileName = (fileName, usedNames) => {
    if (!usedNames.has(fileName)) {
      usedNames.add(fileName);
      return fileName;
    }

    const dotIndex = fileName.lastIndexOf('.');
    const baseName = dotIndex > -1 ? fileName.slice(0, dotIndex) : fileName;
    const extension = dotIndex > -1 ? fileName.slice(dotIndex) : '';

    let duplicateIndex = 2;
    while (true) {
      const nextName = `${baseName}_${duplicateIndex}${extension}`;
      if (!usedNames.has(nextName)) {
        usedNames.add(nextName);
        return nextName;
      }
      duplicateIndex += 1;
    }
  };

  const downloadBlob = (blob, fileName) => {
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
  };

  const buildReceiptManifestCsv = (receiptRows = []) => {
    const header = [
      'receipt_id',
      'receipt_date',
      'saved_at',
      'vendor',
      'amount',
      'currency',
      'category',
      'image_url'
    ];

    const rows = receiptRows.map((receipt) => {
      const amount = parseAmountValue(receipt?.extractedData?.amount);
      const category = getReceiptCategoryLabel(receipt);
      const currency = receipt?.extractedData?.currency || 'EUR';
      const imageUrl = getReceiptImageUrl(receipt);
      const receiptDate = receipt?.extractedData?.date || receipt?.date || '';
      const savedAtTimestamp = getSavedTimestamp(receipt);
      const savedAt = savedAtTimestamp > 0 ? new Date(savedAtTimestamp).toISOString() : '';

      return [
        receipt?.id || '',
        receiptDate,
        savedAt,
        receipt?.extractedData?.vendor || receipt?.vendor || '',
        amount === null ? '' : amount.toFixed(2),
        currency,
        category,
        imageUrl
      ].map(escapeCsvCell).join(',');
    });

    return [header.join(','), ...rows].join('\n');
  };

  const buildArchiveFilename = () => {
    const startRange = sanitizeFilenamePart(filters.startDate || 'all', 'all');
    const endRange = sanitizeFilenamePart(filters.endDate || 'all', 'all');
    const today = new Date().toISOString().slice(0, 10);
    return `receipts_${startRange}_to_${endRange}_${today}.zip`;
  };

  const getFilenameFromContentDisposition = (headerValue) => {
    if (!headerValue || typeof headerValue !== 'string') return '';
    const utf8Match = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch {
        return utf8Match[1];
      }
    }

    const standardMatch = headerValue.match(/filename="?([^"]+)"?/i);
    return standardMatch && standardMatch[1] ? standardMatch[1] : '';
  };

  const exportReceiptsViaApi = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      const authError = new Error('User not authenticated. Please sign in and try again.');
      authError.status = 401;
      throw authError;
    }

    const idToken = await user.getIdToken();
    const response = await fetch(getApiUrl(API_ENDPOINTS.EXPORT_RECEIPTS_ZIP), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      },
      body: JSON.stringify({
        filters,
        workspaceId: activeWorkspace?.id || null
      })
    });

    if (!response.ok) {
      let errorMessage = `Export failed (HTTP ${response.status})`;
      const rawErrorBody = await response.text().catch(() => '');
      const errorText = typeof rawErrorBody === 'string' ? rawErrorBody.trim() : '';

      if (errorText) {
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson?.message || errorJson?.error || errorMessage;
        } catch {
          errorMessage = errorText;
        }
      }

      const exportError = new Error(errorMessage);
      exportError.status = response.status;
      throw exportError;
    }

    const zipBlob = await response.blob();
    if (!zipBlob || zipBlob.size === 0) {
      throw new Error('Export response was empty. Please try again.');
    }

    const contentDisposition = response.headers.get('content-disposition') || '';
    const serverFilename = getFilenameFromContentDisposition(contentDisposition);

    return {
      blob: zipBlob,
      fileName: serverFilename || buildArchiveFilename()
    };
  };

  const buildExportReceiptFilename = (receipt, index, extension) => {
    const receiptDate = receipt?.extractedData?.date || receipt?.date || getSavedTimestamp(receipt);
    const datePart = formatDateForFilename(receiptDate);
    const vendorPart = sanitizeFilenamePart(receipt?.extractedData?.vendor || receipt?.vendor || 'unknown_vendor');
    const idPart = sanitizeFilenamePart(receipt?.id || `receipt_${index + 1}`).slice(0, 24);
    return `${datePart}_${vendorPart}_${idPart}.${extension}`;
  };

  const fetchAllFilteredReceipts = async () => {
    const initialResult = await getReceipts(filters, Math.max(1, pagination.total), 0, {
      workspace: activeWorkspace
    });
    if (initialResult.total > initialResult.receipts.length) {
      const fullResult = await getReceipts(filters, initialResult.total, 0, {
        workspace: activeWorkspace
      });
      return fullResult.receipts;
    }
    return initialResult.receipts;
  };

  const parseAmountValue = (value) => {
    if (value === undefined || value === null) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    const raw = String(value).trim();
    if (!raw) return null;

    let normalized = raw.replace(/[^\d,.-]/g, '');
    if (!normalized) return null;

    const hasComma = normalized.includes(',');
    const hasDot = normalized.includes('.');

    if (hasComma && hasDot) {
      if (normalized.lastIndexOf(',') > normalized.lastIndexOf('.')) {
        normalized = normalized.replace(/\./g, '').replace(',', '.');
      } else {
        normalized = normalized.replace(/,/g, '');
      }
    } else if (hasComma) {
      const parts = normalized.split(',');
      normalized = parts.length === 2 && parts[1].length <= 2
        ? `${parts[0]}.${parts[1]}`
        : normalized.replace(/,/g, '');
    }

    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const parseTimestampValue = (value) => {
    if (!value) return 0;

    if (typeof value?.toMillis === 'function') {
      const millis = value.toMillis();
      return Number.isFinite(millis) ? millis : 0;
    }

    if (typeof value === 'object' && value.seconds !== undefined) {
      const seconds = Number(value.seconds) || 0;
      const nanoseconds = Number(value.nanoseconds) || 0;
      return (seconds * 1000) + Math.round(nanoseconds / 1000000);
    }

    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getSavedTimestamp = (receipt) => {
    if (!receipt) return 0;
    return Math.max(
      parseTimestampValue(receipt.updatedAt),
      parseTimestampValue(receipt.createdAt),
      parseTimestampValue(receipt.processedDate),
      0
    );
  };

  const getSavedDateLabel = (receipt) => {
    const timestamp = getSavedTimestamp(receipt);
    return timestamp > 0 ? new Date(timestamp).toLocaleString() : 'N/A';
  };

  const getReceiptDateLabel = (receipt) => {
    const receiptDate = receipt?.extractedData?.date || receipt?.date;
    if (!receiptDate) return 'N/A';
    const parsed = new Date(receiptDate);
    return Number.isFinite(parsed.getTime()) ? parsed.toLocaleDateString() : 'N/A';
  };

  const getReceiptCategoryLabel = (receipt) => {
    const rawCategory = receipt?.extractedData?.category || receipt?.category;
    if (!rawCategory) return 'Uncategorized';
    return getCategoryDisplayName(normalizeCategoryId(rawCategory));
  };

  const getReceiptAmountLabel = (receipt) => {
    const amount = receipt?.extractedData?.amount;
    if (amount === undefined || amount === null || amount === '') return 'N/A';
    const numeric = parseAmountValue(amount);
    if (numeric === null) return 'N/A';
    const currency = receipt?.extractedData?.currency || 'EUR';
    const currencyPrefix = /^[A-Za-z]{3}$/.test(currency) ? `${currency} ` : currency;
    return `${currencyPrefix}${numeric.toFixed(2)}`;
  };

  // State
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    vendor: '',
    category: '',
    startDate: '',
    endDate: '',
    sortBy: DEFAULT_RECEIPT_SORT
  });
  const [pagination, setPagination] = useState({
    limit: 10,
    offset: 0,
    total: 0
  });
  const [highlightedReceiptId, setHighlightedReceiptId] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState('');

  // Navigation
  const location = useLocation();
  const navigate = useNavigate();
  const rowRefs = useRef({});
  const updatedReceiptId = useMemo(() => location.state?.updatedReceiptId || '', [location.state]);

  const categoryOptions = useMemo(() => {
    const categoriesById = new Map();

    (CATEGORY_OPTIONS.expenses || []).forEach(({ id, name }) => {
      const normalizedId = normalizeCategoryId(id);
      if (!categoriesById.has(normalizedId)) {
        categoriesById.set(normalizedId, name || getCategoryDisplayName(normalizedId));
      }
    });

    receipts.forEach((receipt) => {
      const rawCategory = receipt?.extractedData?.category || receipt?.category || '';
      const normalizedId = normalizeCategoryId(rawCategory);
      if (!normalizedId) return;
      if (!categoriesById.has(normalizedId)) {
        categoriesById.set(normalizedId, getCategoryDisplayName(normalizedId));
      }
    });

    return [...categoriesById.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [receipts]);

  const hasActiveFilters = useMemo(() => (
    Boolean(filters.vendor) ||
    Boolean(filters.category) ||
    Boolean(filters.startDate) ||
    Boolean(filters.endDate) ||
    filters.sortBy !== DEFAULT_RECEIPT_SORT
  ), [filters]);

  // Load receipts on mount and when filters or pagination change
  useEffect(() => {
    const loadReceipts = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await getReceipts(
          filters,
          pagination.limit,
          pagination.offset,
          { workspace: activeWorkspace }
        );

        setReceipts(result.receipts);
        setPagination((prev) => ({
          ...prev,
          total: result.total
        }));
      } catch (loadError) {
        console.error('Error loading receipts:', loadError);
        setError('Failed to load receipts. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadReceipts();
  }, [activeWorkspace, filters, pagination.limit, pagination.offset]);

  useEffect(() => {
    if (!updatedReceiptId || receipts.length === 0) return;
    const matchedReceipt = receipts.find((receipt) => receipt.id === updatedReceiptId);
    if (!matchedReceipt) return;

    setHighlightedReceiptId(updatedReceiptId);
    const targetRow = rowRefs.current[updatedReceiptId];
    if (targetRow) {
      targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    const clearTimer = setTimeout(() => {
      setHighlightedReceiptId('');
    }, 2500);

    navigate('/receipts', { replace: true });
    return () => clearTimeout(clearTimer);
  }, [navigate, receipts, updatedReceiptId]);

  /**
   * Handle filter change
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));

    // Reset pagination when filters change
    setPagination((prev) => ({
      ...prev,
      offset: 0
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      vendor: '',
      category: '',
      startDate: '',
      endDate: '',
      sortBy: DEFAULT_RECEIPT_SORT
    });
    setPagination((prev) => ({
      ...prev,
      offset: 0
    }));
    setExportMessage('');
  };

  const handleExportReceipts = async () => {
    if (isExporting) return;

    const hasStartDate = Boolean(filters.startDate);
    const hasEndDate = Boolean(filters.endDate);

    if (hasStartDate && hasEndDate) {
      const startTime = new Date(filters.startDate).getTime();
      const endTime = new Date(filters.endDate).getTime();
      if (Number.isFinite(startTime) && Number.isFinite(endTime) && startTime > endTime) {
        setError('Start date must be earlier than or equal to end date before exporting.');
        return;
      }
    }

    setIsExporting(true);
    setExportMessage('');
    setError(null);

    try {
      // Prefer server-side export to avoid browser CORS limitations on Firebase Storage downloads.
      try {
        const serverExport = await exportReceiptsViaApi();
        downloadBlob(serverExport.blob, serverExport.fileName);
        setExportMessage('Receipts ZIP downloaded successfully.');
        return;
      } catch (serverError) {
        console.error('Server-side receipt export failed:', serverError);
        if ([400, 401, 403, 404, 422].includes(serverError?.status)) {
          throw serverError;
        }

        // Browser-side fallback can still hit Firebase Storage CORS limits.
        // Keep it opt-in for local troubleshooting only.
        if (process.env.REACT_APP_ENABLE_CLIENT_ZIP_FALLBACK !== '1') {
          throw serverError;
        }
      }

      // Fallback path for transient API errors.
      const receiptsForExport = await fetchAllFilteredReceipts();

      if (receiptsForExport.length === 0) {
        setExportMessage('No receipts match the current filters. Nothing was exported.');
        return;
      }

      const zip = new JSZip();
      const receiptsFolder = zip.folder('receipts') || zip;
      const usedFileNames = new Set();
      const failures = [];
      let downloadedCount = 0;

      for (let index = 0; index < receiptsForExport.length; index += 1) {
        const receipt = receiptsForExport[index];
        const imageUrl = getReceiptImageUrl(receipt);

        if (!imageUrl) {
          failures.push(`Receipt ${receipt?.id || index + 1}: Missing image URL`);
          continue;
        }

        try {
          const blob = await downloadReceiptBlob(receipt, imageUrl);
          const extension = getImageExtension(imageUrl, blob.type);
          const baseFileName = buildExportReceiptFilename(receipt, index, extension);
          const fileName = ensureUniqueFileName(baseFileName, usedFileNames);

          receiptsFolder.file(fileName, blob);
          downloadedCount += 1;
        } catch (fetchError) {
          const reason = fetchError?.message || 'Unknown error';
          failures.push(`Receipt ${receipt?.id || index + 1}: ${reason}`);
        }
      }

      zip.file('receipt_manifest.csv', buildReceiptManifestCsv(receiptsForExport));

      if (failures.length > 0) {
        zip.file(
          'missing_receipts.txt',
          ['Some receipts could not be added to the ZIP:', '', ...failures].join('\n')
        );
      }

      if (downloadedCount === 0) {
        throw new Error('No receipt files were downloadable. Check receipt image URLs and permissions.');
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      downloadBlob(zipBlob, buildArchiveFilename());

      if (failures.length > 0) {
        setExportMessage(
          `Exported ${downloadedCount} of ${receiptsForExport.length} receipts. See missing_receipts.txt in the ZIP for skipped files.`
        );
      } else {
        setExportMessage(`Exported ${downloadedCount} receipts to ZIP successfully.`);
      }
    } catch (exportError) {
      console.error('Error exporting receipts:', exportError);
      if (exportError?.status === 404) {
        setError('Receipt ZIP export endpoint is not available yet. Deploy the latest Cloud Functions and try again.');
      } else {
        setError(exportError.message || 'Failed to export receipts. Please try again.');
      }
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Handle page change
   */
  const handlePageChange = (newPage) => {
    const newOffset = (newPage - 1) * pagination.limit;

    setPagination((prev) => ({
      ...prev,
      offset: newOffset
    }));
  };

  /**
   * Handle receipt click
   */
  const handleReceiptClick = (receiptId) => {
    navigate(`/receipts/${receiptId}`, {
      state: {
        receiptIds: receipts.map((receipt) => receipt.id)
      }
    });
  };

  /**
   * Handle receipt deletion
   */
  const handleDeleteReceipt = async (receiptId, e) => {
    e.stopPropagation(); // Prevent navigation

    if (!window.confirm('Are you sure you want to delete this receipt?')) {
      return;
    }

    try {
      await deleteReceipt(receiptId, { workspace: activeWorkspace });

      // Remove the receipt from the list
      setReceipts((prev) => prev.filter((receipt) => receipt.id !== receiptId));

      // Update the total count
      setPagination((prev) => ({
        ...prev,
        total: prev.total - 1
      }));
    } catch (deleteError) {
      console.error('Error deleting receipt:', deleteError);
      alert('Failed to delete receipt. Please try again.');
    }
  };

  // Calculate pagination info
  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  return (
    <div className="receipt-list bg-white rounded-2xl shadow border border-gray-100 overflow-hidden">
      <div className="px-4 sm:px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-indigo-50/70 via-white to-white">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Receipts Dashboard</h2>
            <p className="text-sm text-gray-600 mt-1">
              Sorted by latest saved entry by default so newly saved receipts appear first.
            </p>
          </div>
          <div className="inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-indigo-100 text-indigo-700 text-sm font-medium self-start lg:self-auto">
            {pagination.total} receipt{pagination.total === 1 ? '' : 's'}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="sm:col-span-2 lg:col-span-2">
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              Vendor
            </label>
            <input
              type="text"
              name="vendor"
              value={filters.vendor}
              onChange={handleFilterChange}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Search vendor"
            />
          </div>

          <div className="sm:col-span-1 lg:col-span-1">
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              Category
            </label>
            <select
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All categories</option>
              {categoryOptions.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-1 lg:col-span-1">
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              Sort
            </label>
            <select
              name="sortBy"
              value={filters.sortBy}
              onChange={handleFilterChange}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {RECEIPT_SORT_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-1 lg:col-span-1">
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              Start date
            </label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="sm:col-span-1 lg:col-span-1">
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              End date
            </label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-gray-500">
            Current order: <span className="font-medium text-gray-700">
              {RECEIPT_SORT_OPTIONS.find((option) => option.id === filters.sortBy)?.label || 'Latest entry first'}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleExportReceipts}
              disabled={isExporting || loading || pagination.total === 0}
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-indigo-200 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? 'Preparing ZIP...' : 'Download ZIP'}
            </button>
            <button
              type="button"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
              className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear filters
            </button>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          ZIP export includes all receipts that match the current filters and date range.
        </p>
      </div>

      <div className="p-4 sm:p-6">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 p-4 rounded-md mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {location.state?.saveSuccess && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md mb-4 text-sm">
            Receipt saved. Continue selecting receipts below.
          </div>
        )}

        {exportMessage && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-md mb-4 text-sm">
            {exportMessage}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : (
          <>
            {receipts.length > 0 ? (
              <>
                {/* Mobile cards */}
                <div className="md:hidden space-y-3">
                  {receipts.map((receipt) => (
                    <div
                      key={receipt.id}
                      ref={(node) => {
                        if (node) {
                          rowRefs.current[receipt.id] = node;
                        }
                      }}
                      className={`rounded-xl border p-3 cursor-pointer transition ${highlightedReceiptId === receipt.id ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200 hover:border-indigo-300'}`}
                      onClick={() => handleReceiptClick(receipt.id)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          {receipt.imageUrl ? (
                            <img
                              src={receipt.imageUrl}
                              alt="Receipt"
                              className="h-12 w-12 object-cover rounded-md"
                            />
                          ) : (
                            <div className="h-12 w-12 bg-gray-100 rounded-md flex items-center justify-center">
                              <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-900 truncate">
                              {receipt.extractedData?.vendor || 'Unknown Vendor'}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              Receipt #{receipt.id.substring(0, 8)}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => handleDeleteReceipt(receipt.id, e)}
                          className="px-2 py-1 text-xs font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                        <div className="rounded-md bg-gray-50 px-2.5 py-2">
                          <div className="text-gray-500">Receipt date</div>
                          <div className="font-medium text-gray-800">{getReceiptDateLabel(receipt)}</div>
                        </div>
                        <div className="rounded-md bg-gray-50 px-2.5 py-2">
                          <div className="text-gray-500">Saved</div>
                          <div className="font-medium text-gray-800">{getSavedDateLabel(receipt)}</div>
                        </div>
                        <div className="rounded-md bg-gray-50 px-2.5 py-2">
                          <div className="text-gray-500">Amount</div>
                          <div className="font-medium text-gray-800">{getReceiptAmountLabel(receipt)}</div>
                        </div>
                        <div className="rounded-md bg-gray-50 px-2.5 py-2">
                          <div className="text-gray-500">Category</div>
                          <div className="font-medium text-gray-800">{getReceiptCategoryLabel(receipt)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Receipt
                        </th>
                        <th scope="col" className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Receipt Date
                        </th>
                        <th scope="col" className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Saved
                        </th>
                        <th scope="col" className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Vendor
                        </th>
                        <th scope="col" className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th scope="col" className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th scope="col" className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {receipts.map((receipt) => (
                        <tr
                          key={receipt.id}
                          ref={(node) => {
                            if (node) {
                              rowRefs.current[receipt.id] = node;
                            }
                          }}
                          className={`cursor-pointer ${highlightedReceiptId === receipt.id ? 'bg-green-50' : 'hover:bg-gray-50'}`}
                          onClick={() => handleReceiptClick(receipt.id)}
                        >
                          <td className="px-5 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {receipt.imageUrl ? (
                                <img
                                  src={receipt.imageUrl}
                                  alt="Receipt"
                                  className="h-10 w-10 object-cover rounded-md mr-3"
                                />
                              ) : (
                                <div className="h-10 w-10 bg-gray-100 rounded-md mr-3 flex items-center justify-center">
                                  <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </div>
                              )}
                              <div className="text-sm font-medium text-gray-900">
                                Receipt #{receipt.id.substring(0, 8)}
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900">
                            {getReceiptDateLabel(receipt)}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-600">
                            {getSavedDateLabel(receipt)}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900">
                            {receipt.extractedData?.vendor || 'Unknown Vendor'}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900">
                            {getReceiptAmountLabel(receipt)}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span className="px-2.5 py-1 inline-flex text-xs font-semibold rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                              {getReceiptCategoryLabel(receipt)}
                            </span>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              type="button"
                              onClick={(e) => handleDeleteReceipt(receipt.id, e)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="py-10 text-center text-gray-500">
                No receipts found. Try adjusting your filters or upload a new receipt.
              </div>
            )}

            {/* Pagination */}
            {receipts.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{pagination.offset + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.offset + pagination.limit, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.total}</span> receipts
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  <div className="text-sm text-gray-600 px-2">
                    Page {currentPage} of {totalPages}
                  </div>

                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ReceiptList;
